<template>
    <div>
        <h2>{{$t('TWO_FACTOR_AUTHENTICATION')}}</h2>
        <div v-if="!twoFactorAuthenticationEnabled">
            <div v-if="recoveryCodes.length == 0">
                <p>To use an authenticator app go through the following steps:</p>
                <ol class="list">
                    <li>
                        <p>
                            Download a two-factor authenticator app like Microsoft Authenticator for
                            <a href="https://go.microsoft.com/fwlink/?Linkid=825071">Windows Phone</a>,
                            <a href="https://go.microsoft.com/fwlink/?Linkid=825072">Android</a> and
                            <a href="https://go.microsoft.com/fwlink/?Linkid=825073">iOS</a> or
                            Google Authenticator for
                            <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&amp;hl=en">Android</a> and
                            <a href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8">iOS</a>.
                        </p>
                    </li>
                    <li v-if="twoFactorAuthenticatorMeta != null">
                        <p>Scan the QR Code or enter this key <kbd>{{twoFactorAuthenticatorMeta.sharedKey}}</kbd> into your two factor authenticator app. Spaces and casing do not matter.</p>
                        <div>
                            <qrcode :value="twoFactorAuthenticatorMeta.authenticatorUri" size="200"></qrcode>
                        </div>
                    </li>
                    <li>
                        <p>
                            Once you have scanned the QR code or input the key above, your two factor authentication app will provide you
                            with a unique code. Enter the code in the confirmation box below.
                        </p>
                        <div class="row">
                            <div class="col-md-6">
                                <form method="post" @submit.prevent="verifyCode">
                                    <div class="form-group">
                                        <label class="control-label">Verification Code</label>
                                        <b-form-input type="text" v-model="verificationCode" :state="correctVerificationCode" class="form-control" autocomplete="off" />
                                    </div>
                                    <button type="submit" class="btn btn-primary">{{$t('VERIFY')}}</button>
                                </form>
                            </div>
                        </div>
                    </li>
                </ol>
            </div>
            <div v-else>
                <div class="alert alert-warning" role="alert">
                    <p>
                        <span class="glyphicon glyphicon-warning-sign"></span>
                        <strong>Put these codes in a safe place.</strong>
                    </p>
                    <p>
                        If you lose your device and don't have the recovery codes you will lose access to your account.
                    </p>
                </div>
                <div class="row">
                    <div class="col-md-12">
                        <span v-for="(code, index) in recoveryCodes" :key="index">
                            <code>{{code}}</code><br />
                        </span>
                    </div>
                    <div class="mt-3">
                        <button class="btn btn-primary" @click="checkUserForTwoFactorAuthentication">Back to 2FA Settings</button>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <recovery-codes-alert :codes="leftRecoveryCodes"></recovery-codes-alert>
            <div class="card mb-3">
                <div class="card-body">
                    <h4>{{$t('CONFIGURATION')}}</h4>
                    <button class="btn btn-primary" @click="generateNewRecoveryCodes">{{$t('RESET_RECOVERY_CODES')}}</button>
                    <div v-if="recoveryCodesNewGeneration.length > 0" class="mt-3">
                        <div class="alert alert-warning" role="alert">
                            <p>
                                <span class="glyphicon glyphicon-warning-sign"></span>
                                <strong>{{$t('PUT_CODES_IN_A_SAFE_PLACE')}}</strong>
                            </p>
                            <p>
                                {{$t('LOSE_ACCESS_WARNING')}}
                            </p>
                        </div>
                        <div class="row">
                            <div class="col-md-12">
                                <span v-for="(code, index) in recoveryCodesNewGeneration" :key="index+100">
                                    <code>{{code}}</code><br />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>import QrcodeVue from 'qrcode.vue';
    import RecoveryCodesAlert from './RecoveryCodesAlert.vue';
    export default {
        components: {
            'qrcode': QrcodeVue,
            'recovery-codes-alert': RecoveryCodesAlert
        },
        data() {
            return {
                twoFactorAuthenticatorMeta: null,
                verificationCode: '',
                correctVerificationCode: null,
                recoveryCodes: [],
                twoFactorAuthenticationEnabled: false,
                leftRecoveryCodes: 0,
                recoveryCodesNewGeneration: []
            }
        },
        methods: {
            loadTwoFactorAuthenticatorMeta: function () {
                let self = this;
                this.$http.get("/api/manage/2fa/meta")
                    .then(response => {
                        self.twoFactorAuthenticatorMeta = response.data;
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            verifyCode: function () {
                let self = this;
                this.$http.post("/api/manage/2fa/verify-code", {
                    "code": this.verificationCode
                })
                    .then(response => {
                        if (response.data.length > 0) {
                            self.correctVerificationCode = true;
                            self.recoveryCodes = response.data;
                        }
                        else {
                            self.correctVerificationCode = false;
                        }
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            checkUserForTwoFactorAuthentication: function () {
                let self = this;
                this.$http.post("/api/manage/2fa/check")
                    .then(response => {
                        self.twoFactorAuthenticationEnabled = response.data;
                        if (self.twoFactorAuthenticationEnabled == false) {
                            self.loadTwoFactorAuthenticatorMeta();
                        }
                        else {
                            self.countRecoveryCodes();
                        }
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            countRecoveryCodes: function () {
                let self = this;
                this.$http.get("/api/manage/2fa/left-codes")
                    .then(response => {
                        self.leftRecoveryCodes = response.data;
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            generateNewRecoveryCodes: function () {
                let self = this;
                this.$http.post("/api/manage/2fa/generate-new-recovery-codes")
                    .then(response => {
                        self.recoveryCodesNewGeneration = response.data;
                        self.countRecoveryCodes();
                    })
                    .catch(error => {
                        console.log(error);
                    });
            },
            clearTwoFactorAuthenticationData: function () {
                this.twoFactorAuthenticatorMeta = null;
                this.verificationCode = '';
                this.correctVerificationCode = null;
                this.recoveryCodes = [];
                this.twoFactorAuthenticationEnabled = false;
                this.leftRecoveryCodes = 0;
                this.recoveryCodesNewGeneration = [];
            }
        },
        mounted: function () {
            this.checkUserForTwoFactorAuthentication();
        }
    }</script>

<style lang="scss" scoped>

</style>
